import React from 'react'
import { useDownload } from 'features/download-widget'
import {
  getDownloadStatus,
  startUnifiedTargetExportV2,
} from 'services/downloadsApi'
import { UnifiedTargetType } from 'services/opportunities/targetTypeMap'
import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'
import {
  BaseExportModal,
  ExportModalColumn,
  ExportModalProps,
} from './ExportModal'

/**
 * Config for one target table's export modal (FIR-6997 consolidation).
 *
 * Every opportunities export modal was the same ~100-line component around a
 * per-vertical column-group array and copy strings; this captures exactly those
 * axes. All exports run through the unified `/opportunities/[doors/]bulk/export/`
 * endpoints, which resolve a select-all through the SAME unified engine that
 * rendered the rows (FIR-7822) and then dispatch to the vertical's existing
 * export use case server-side, so columns/headers/file output are unchanged.
 */
export interface TargetExportModalConfig {
  /** Unified target type the export resolves + dispatches on. */
  targetType: UnifiedTargetType
  /** Route through the doors-scoped unified endpoint (door-proxy id space). */
  isDoors: boolean
  /**
   * Column groups shown in the modal. A hook, so a config can gate groups on
   * feature flags (e.g. lodging's Hotel Amenities group).
   */
  useColumnGroups: () => ExportModalColumn[]
  title: string
  subTitle: string
  submitText: string
  /** Download-widget item name, e.g. `Exporting Universities...` */
  progressName: string
  /** Download-widget label, e.g. `Universities`. */
  downloadLabel: string
}

/**
 * Build a vertical's export modal component from config. Returns a component
 * matching the `ExportModal` slot contract (`ExportModalProps`).
 */
export function buildTargetExportModal(
  config: TargetExportModalConfig
): React.FC<ExportModalProps> {
  function TargetExportModal(props: ExportModalProps) {
    const columns = config.useColumnGroups()
    const selectedColumns = useColumnStoreContext(
      (state) => state.selectedColumns
    )
    const { startDownload } = useDownload()

    const onSubmit = async (
      selectedCols: string[],
      exportType: 'csv' | 'xlsx'
    ) => {
      await startDownload({
        items: [{ id: 0, name: config.progressName }],
        initiate: async () => {
          const { sort, ...filterWithoutSort } = (props.filters ??
            {}) as Record<string, unknown>
          const response = await startUnifiedTargetExportV2(
            {
              selected_columns: selectedCols,
              export_type: exportType,
              filter_params: {
                ...filterWithoutSort,
                // Product selection scopes LTV columns (restaurant tables);
                // omitted for tables without a product selector.
                ...(props.productSelected !== undefined
                  ? { product_id: props.productSelected }
                  : {}),
                include_ids: props.selectedIds ?? [],
                exclude_ids: props.excludeIds ?? [],
              },
              sort: sort as string | undefined,
            },
            config.targetType,
            config.isDoors
          )
          return { type: 'async', requestId: response.request_id }
        },
        getStatus: getDownloadStatus,
        label: config.downloadLabel,
      })
    }

    return (
      <BaseExportModal
        title={config.title}
        subTitle={config.subTitle}
        submitText={config.submitText}
        columns={columns}
        selectedColumns={selectedColumns}
        onSubmit={onSubmit}
        show={props.show}
        handleClose={props._handleClose}
      />
    )
  }
  TargetExportModal.displayName = `TargetExportModal(${config.targetType}${
    config.isDoors ? ':doors' : ''
  })`
  return TargetExportModal
}
